import { EXPERIENCE_TYPES } from "constants/experiences"

/**
 * Derives which wizard steps are "complete" purely from the saved trip data.
 *
 * The wizard's green tab ticks are otherwise tracked in the browser's sessionStorage,
 * which is empty for a trip created elsewhere (mobile app, another browser/device).
 * Seeding from the trip itself makes the ticks reflect actual saved progress for any
 * trip, regardless of which client created it.
 *
 * A step counts as complete when its meaningful data is present on the trip. Optional
 * steps (resources, additional_info) only tick when they actually carry data — there's
 * no server "completed" flag per step to rely on. Keyed off trip.experience_type so it
 * works without the URL ?type= param.
 *
 * @param {object} trip - server trip payload
 * @returns {Set<string>} step ids that have data (matches stepConfig ids)
 */
export const deriveCompletedSteps = (trip) => {
  const done = new Set()
  if (!trip?.id) return done

  const isTicketed = trip.experience_type === EXPERIENCE_TYPES.ticketed
  const hasItems = (value) => Array.isArray(value) && value.length > 0
  // The API sends the listing's one category as category_id (and a `category` object), never `categories`.
  const hasCategory = trip.category_id != null

  // A draft can be saved with a step half-filled, so each tick needs what that step requires.

  // details — core required fields
  if (trip.title && trip.description && hasCategory && hasItems(trip.subcategory_ids)) done.add("details")

  // ticket_type (ticketed only) — at least one ticket type, the ticket limit and description
  if (isTicketed && hasItems(trip.ticket_types) && trip.max_tickets != null && trip.ticket_description) done.add("ticket_type")

  // scheduling — ticketed needs a duration and start times; private needs at least one availability schedule
  if (isTicketed ? trip.duration_value != null && hasItems(trip.time_slots) : hasItems(trip.availability_schedules)) {
    done.add("scheduling")
  }

  // pricing — ticketed has priced ticket types; private has booking options (trip_prices)
  const pricingDone = isTicketed
    ? (trip.ticket_types || []).some((ticketType) => ticketType.price > 0)
    : hasItems(trip.trip_prices)
  if (pricingDone) done.add("pricing")

  // resources — equipment/staff assigned or auto-assign enabled
  if (hasItems(trip.boat_ids) || hasItems(trip.assigned_staff_ids) || trip.auto_assign_equipment) {
    done.add("resources")
  }

  // location — saved meeting point
  if (trip.address || (trip.latitude != null && trip.longitude != null)) done.add("location")

  // media — any uploaded photo/media
  if (hasItems(trip.media) || hasItems(trip.photos) || hasItems(trip.cropped_photos)) done.add("media")

  // additional_info — waivers or a question template attached. Add-ons are NOT
  // part of this step (they live on the Details page), so they don't count here.
  if (hasItems(trip.digital_waiver_experiences) || trip.question_template_id) {
    done.add("additional_info")
  }

  // preview — the wizard was carried through to completion
  if (trip.wizard_completed) done.add("preview")

  return done
}
